import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { LandingScope } from 'mormaii-web-presentation/landing'

import { MetricIcon, Brand, LicensedSeal } from '../../components/icons'

import Css from './landing.module.scss'

/**
 * A pagina que vende o produto.
 *
 * A copia inteira vem do scope - ver `LandingScope`, que explica por que texto de
 * produto nao mora na view. O que esta aqui e SO o desenho, e e por isso que o
 * mesmo conteudo pode ser desenhado tambem pelo renderizador React sem divergir uma
 * palavra.
 *
 * ## O que foi tomado da referencia, e o que nao foi
 *
 * A estrutura e a de `zenithwellness.health`: heroi com promessa curta, grade de
 * metricas, tres passos, secoes de recurso alternadas, prova social, fecho. O que
 * NAO foi tomado sao as fotos de atleta - aquelas sao do cliente de la, e uma foto
 * generica de banco de imagens diria menos do que nada. No lugar delas, o produto
 * mostra a si mesmo: as proprias faixas de status, os proprios numeros.
 */
export function LandingView(props: ViewProps<LandingScope>): JSX.Element {
    const onSignIn = () => void props.scope.onSignIn()

    return (
        <div class={Css.page}>
            <Header scope={props.scope} onSignIn={onSignIn} />
            <Hero scope={props.scope} onSignIn={onSignIn} />
            <Metrics scope={props.scope} />
            <Steps scope={props.scope} />
            <Features scope={props.scope} />
            <Voices scope={props.scope} />
            <Closing scope={props.scope} onSignIn={onSignIn} />
            <Footer />
        </div>
    )
}

// ========== O CABECALHO ==========

type WithActions = { scope: LandingScope; onSignIn: () => void }

/** Leva a secao ao topo sem tocar no fragmento - ver o comentario no cabecalho. */
function scrollToSection(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function Header(props: WithActions): JSX.Element {
    return (
        <header class={Css.top}>
            <div class={Css.bar}>
                {/*
                    `shrink-0` e `whitespace-nowrap` nos tres blocos: sem eles o flex
                    encolhe a marca e o botao ate quebrarem em duas linhas, e a barra
                    de 16 dobra de altura num telefone.
                */}
                <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} class={Css.brand}>
                    <Brand class={Css.brandIcon} />
                    <span class={Css.brandName}>
                        Mormaii <span class={Css.brandAccent}>Mais Saúde</span>
                    </span>
                </button>

                {/*
                    BOTAO, e nao `<a href="#secao">`.

                    O fragmento do endereco e do ROTEADOR: o Cube guarda o place em
                    `#/landing`, e uma ancora de pagina o substituiria por
                    `#how-it-works`, apagando a rota. A aplicacao se recupera no
                    proximo carregamento, mas ate la a barra mostra um endereco que
                    ela nao produziu - e na area restrita, onde o endereco viaja
                    selado, um endereco desses e recusado.
                */}
                <nav class={Css.jumps}>
                    <button type="button" class={Css.jump} onClick={() => scrollToSection('what-we-track')}>
                        O que acompanha
                    </button>
                    <button type="button" class={Css.jump} onClick={() => scrollToSection('how-it-works')}>
                        Como funciona
                    </button>
                    <button type="button" class={Css.jump} onClick={() => scrollToSection('features')}>
                        Recursos
                    </button>
                </nav>

                <div class={Css.actions}>
                    {/*
                        No telefone so a porta fica na barra: os dois botoes nao cabem
                        ao lado da marca, e "Criar conta" esta a um dedo de distancia
                        logo abaixo, no heroi.
                    */}
                    <button type="button" onClick={props.onSignIn} class={Css.signIn}>
                        {props.scope.signInLabel}
                    </button>
                    <Show when={props.scope.contactEmail}>
                        <a href={`mailto:${props.scope.contactEmail}`} class={Css.contact}>
                            {props.scope.contactLabel}
                        </a>
                    </Show>
                </div>
            </div>
        </header>
    )
}

// ========== O HEROI ==========

function Hero(props: WithActions): JSX.Element {
    return (
        <section class={Css.hero}>
            <div class={Css.heroGrid}>
                <div class={Css.heroTexts}>
                    <span class={Css.badge}>
                        <span class={Css.badgeDot} />
                        Saúde corporativa com dado contínuo
                    </span>

                    <h1 class={Css.headline}>{props.scope.headline}</h1>

                    <p class={Css.subheadline}>{props.scope.subheadline}</p>

                    <div class={Css.buttons}>
                        <button type="button" onClick={props.onSignIn} class={Css.primary}>
                            {props.scope.signInLabel}
                        </button>
                        <Show when={props.scope.contactEmail}>
                            <a href={`mailto:${props.scope.contactEmail}`} class={Css.secondary}>
                                {props.scope.contactLabel}
                            </a>
                        </Show>
                    </div>

                    <p class={Css.promise}>{props.scope.promise}</p>
                </div>

                <DemoCard />
            </div>
        </section>
    )
}

/**
 * O produto mostrando a si mesmo, no lugar da foto.
 *
 * Nao e captura de tela: e uma reproducao pequena do que a lista de colaboradores
 * faz - o contador de criticos, as faixas coloridas, a ultima leitura. Reproduzir
 * em vez de fotografar mantem o desenho nitido em qualquer tela e nao envelhece
 * quando a tela real mudar de espacamento.
 */
function DemoCard(): JSX.Element {
    const rows = [
        { name: 'Edmar Quirino', critical: 2, heartRate: '43 bpm', band: 'ruim' },
        { name: 'Heitor Bastos', critical: 1, heartRate: '99 bpm', band: 'atencao' },
        { name: 'Caio Menezes', critical: 0, heartRate: '72 bpm', band: 'bom' },
        { name: 'Adriano Peçanha', critical: 0, heartRate: '68 bpm', band: 'bom' }
    ]

    const bandColor = (band: string) =>
        band === 'bad' ? Css.demoBad : band === 'warning' ? Css.demoWatch : Css.demoGood

    return (
        <div class={Css.demo}>
            {/* O brilho atras do cartao, que o descola do fundo sem uma sombra dura. */}
            <div class={Css.glow} />

            <div class={Css.demoCard}>
                <div class={Css.demoTop}>
                    <p class={Css.demoTitle}>Colaboradores</p>
                    <span class={Css.demoCompany}>Atlético Ribeira FC</span>
                </div>

                <div class={Css.demoColumns}>
                    <span>Crít.</span>
                    <span>Nome</span>
                    <span>Freq. cardíaca</span>
                </div>

                <For each={rows}>
                    {(row) => (
                        <div class={Css.demoRow}>
                            <span class={Css.demoCritical} classList={{ [Css.some]: row.critical > 0 }}>
                                {row.critical}
                            </span>
                            <span class={Css.demoName}>{row.name}</span>
                            <span class={Css.demoReading}>
                                <span class={`${Css.demoDot} ${bandColor(row.band)}`} />
                                {row.heartRate}
                            </span>
                        </div>
                    )}
                </For>

                <p class={Css.demoFooter}>A lista abre ordenada por quem tem mais indicadores fora da faixa.</p>
            </div>
        </div>
    )
}

// ========== AS METRICAS ==========

function Metrics(props: { scope: LandingScope }): JSX.Element {
    return (
        <section id="what-we-track" class={Css.metrics}>
            <div class={Css.stripe}>
                <h2 class={Css.sectionHeadline}>{props.scope.metricsTitle}</h2>

                <ul class={Css.metricsGrid}>
                    <For each={props.scope.metrics}>
                        {(metric) => (
                            <li class={Css.metric}>
                                <MetricIcon name={metric.icon} class={Css.metricIcon} />
                                <p class={Css.metricName}>{metric.label}</p>
                                <p class={Css.metricNote}>{metric.hint}</p>
                            </li>
                        )}
                    </For>
                </ul>
            </div>
        </section>
    )
}

// ========== OS PASSOS ==========

function Steps(props: { scope: LandingScope }): JSX.Element {
    return (
        <section id="how-it-works" class={Css.steps}>
            <div class={Css.callout}>
                <h2 class={Css.calloutTitle}>{props.scope.stepsTitle}</h2>
                <p class={Css.calloutText}>{props.scope.stepsSubtitle}</p>
            </div>

            <ol class={Css.stepsGrid}>
                <For each={props.scope.steps}>
                    {(step, index) => (
                        <li class={Css.step}>
                            <span class={Css.stepNumber}>{index() + 1}</span>
                            <h3 class={Css.stepTitle}>{step.title}</h3>
                            <p class={Css.stepText}>{step.text}</p>
                        </li>
                    )}
                </For>
            </ol>
        </section>
    )
}

// ========== OS RECURSOS ==========

function Features(props: { scope: LandingScope }): JSX.Element {
    return (
        <section id="features" class={Css.features}>
            <div class={Css.featuresBody}>
                <For each={props.scope.features}>
                    {(feature, index) => (
                        <article class={Css.feature} classList={{ [Css.flipped]: index() % 2 === 1 }}>
                            <div class={Css.featureTexts}>
                                <p class={Css.eyebrow}>{feature.eyebrow}</p>
                                <h3 class={Css.featureTitle}>{feature.title}</h3>
                                <p class={Css.featureText}>{feature.text}</p>

                                <ul class={Css.bullets}>
                                    <For each={feature.bullets}>
                                        {(bullet) => (
                                            <li class={Css.bullet}>
                                                <svg
                                                    class={Css.bulletCheck}
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="2"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    aria-hidden="true"
                                                >
                                                    <path d="m3 8.5 3 3 7-7" />
                                                </svg>
                                                {bullet}
                                            </li>
                                        )}
                                    </For>
                                </ul>
                            </div>

                            <FeaturePanel index={index()} />
                        </article>
                    )}
                </For>
            </div>
        </section>
    )
}

/** Uma ilustracao por recurso, feita dos proprios elementos do painel. */
function FeaturePanel(props: { index: number }): JSX.Element {
    return (
        <div class={Css.illustrationCard}>
            {props.index === 0 ? (
                <ListIllustration />
            ) : props.index === 1 ? (
                <ChartIllustration />
            ) : (
                <ReportIllustration />
            )}
        </div>
    )
}

function ListIllustration(): JSX.Element {
    const bars = [92, 74, 61, 48, 35, 22]
    return (
        <div class={Css.illustration}>
            <p class={Css.illustrationTitle}>Em atenção por pessoa</p>
            {/*
                `h-full` e `justify-end` na COLUNA, e nao so no pai.
                Altura em porcentagem mede contra a altura do pai, e um item de
                flex-column tem altura automatica - sem isto a barra nasce com zero
                e a ilustracao aparece vazia.
            */}
            <div class={Css.bars}>
                <For each={bars}>
                    {(height, i) => (
                        <div class={Css.column}>
                            <div
                                class={Css.barsBar}
                                classList={{
                                    [Css.critical]: i() < 2,
                                    [Css.watch]: i() === 2,
                                    [Css.calm]: i() > 2
                                }}
                                style={{ height: `${height}%` }}
                            />
                            <span class={Css.floor} />
                        </div>
                    )}
                </For>
            </div>
        </div>
    )
}

function ChartIllustration(): JSX.Element {
    const points = [30, 45, 38, 62, 55, 78, 70, 85, 72, 90]
    const path = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i / (points.length - 1)) * 100} ${100 - v}`).join(' ')

    return (
        <div class={Css.illustration}>
            <p class={Css.illustrationTitle}>Média do período</p>
            <svg class={Css.curve} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <path d={`${path} L 100 100 L 0 100 Z`} fill="var(--accent-soft)" opacity="0.7" />
                <path
                    d={path}
                    fill="none"
                    stroke="var(--accent)"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                    stroke-linecap="round"
                />
            </svg>
            <div class={Css.legend}>
                <span class={Css.legendItem}>
                    <span class={`${Css.legendDot} ${Css.legendBrand}`} /> Frequência cardíaca
                </span>
                <span class={Css.legendItem}>
                    <span class={`${Css.legendDot} ${Css.legendVital}`} /> Dentro da faixa
                </span>
            </div>
        </div>
    )
}

function ReportIllustration(): JSX.Element {
    const cells = Array.from({ length: 35 }, (_, i) => (i * 7) % 3)
    return (
        <div class={Css.illustration}>
            <p class={Css.illustrationTitle}>Relatório de sono</p>
            <div class={Css.weeks}>
                <For each={cells}>
                    {(band) => (
                        <div
                            class={Css.day}
                            classList={{
                                [Css.daySky]: band === 0,
                                [Css.dayAmber]: band === 1,
                                [Css.dayRose]: band === 2
                            }}
                        />
                    )}
                </For>
            </div>
            <p class={Css.illustrationNote}>Cinco semanas, uma coluna por atleta, seis tabelas.</p>
        </div>
    )
}

// ========== A PROVA SOCIAL ==========

function Voices(props: { scope: LandingScope }): JSX.Element {
    return (
        <section class={Css.voices}>
            <h2 class={Css.sectionHeadline}>{props.scope.voicesTitle}</h2>

            <div class={Css.voicesGrid}>
                <For each={props.scope.voices}>
                    {(voice) => (
                        <figure class={Css.voice}>
                            <blockquote class={Css.quote}>“{voice.quote}”</blockquote>
                            <figcaption class={Css.byline}>
                                <span class={Css.who}>{voice.who}</span>
                                <span class={Css.role}>{voice.role}</span>
                            </figcaption>
                        </figure>
                    )}
                </For>
            </div>
        </section>
    )
}

// ========== O FECHO ==========

function Closing(props: WithActions): JSX.Element {
    return (
        <section class={Css.closing}>
            <div class={Css.darkBox}>
                <div class={Css.closingHaloTop} />
                <div class={Css.closingHaloBottom} />

                <div class={Css.closingBody}>
                    <h2 class={Css.closingTitle}>{props.scope.closingTitle}</h2>
                    <p class={Css.closingText}>{props.scope.closingText}</p>

                    <div class={Css.closingButtons}>
                        <button type="button" onClick={props.onSignIn} class={Css.closingPrimary}>
                            {props.scope.signInLabel}
                        </button>
                        <Show when={props.scope.contactEmail}>
                            <a href={`mailto:${props.scope.contactEmail}`} class={Css.closingSecondary}>
                                {props.scope.contactLabel}
                            </a>
                        </Show>
                    </div>
                </div>
            </div>
        </section>
    )
}

function Footer(): JSX.Element {
    return (
        <footer class={Css.footer}>
            <div class={Css.footerBody}>
                <div class={Css.footerBrand}>
                    <Brand class={Css.footerIcon} />
                    <span class={Css.footerName}>Mormaii Mais Saúde</span>
                </div>
                <div class={Css.footerLegal}>
                    <LicensedSeal class={Css.footerSeal} />
                    <p class={Css.footerText}>
                        © {new Date().getFullYear()} Mormaii Mais Saúde · Todos os direitos reservados
                    </p>
                </div>
            </div>
        </footer>
    )
}
